import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styles from "./QuestionsEditor.module.css";

export default function QuestionForm({ question, positionInPage, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, pageNumbers = [] }) {
    const { t } = useTranslation();
    const q = question || {};

    const types = useMemo(() => ([
        'SC',
        'MC',
        'Open',
        'OpenList',
        'Ladder',
        'Label',
        'SelectText',
    ]), []);

    const set = (patch) => onChange?.({ ...q, ...patch });
    const setDetails = (patch) => {
        const next = { ...(q.details || {}) };
        Object.keys(patch || {}).forEach((key) => {
            if (patch[key] === undefined) {
                delete next[key];
            } else {
                next[key] = patch[key];
            }
        });
        set({ details: next });
    };

    return (
        <div className={styles.qCard}>
            <div className={styles.qHeader}>
                <div className={styles.qTitle}>
                    {t('editor.question')} {(q?.questionID)}
                </div>
                <div className={styles.qActions}>
                    <button type="button" className={styles.iconBtn} onClick={onMoveUp} disabled={isFirst}>↑</button>
                    <button type="button" className={styles.iconBtn} onClick={onMoveDown} disabled={isLast}>↓</button>
                    <button type="button" className={styles.iconBtn} onClick={onRemove}>{t('actions.remove', { defaultValue: 'Usuń' })}</button>
                </div>
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr auto', gap: 8, alignItems: 'end' }}>
                    <label>
                        {t('editor.pageNumber', { defaultValue: 'Numer strony' })}
                        <select className={styles.select} value={Number(q.pageNumber || 1)} onChange={(e) => set({ pageNumber: Number(e.target.value) })}>
                            {Array.from(new Set(pageNumbers)).sort((a, b) => a - b).map((n) => (
                                <option key={n} value={n}>{t('editor.pageTitle', { num: n, defaultValue: `Strona ${n}` })}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        {t('editor.type', { defaultValue: 'Typ pytania' })}
                        <select className={styles.select} value={q.questionType || 'SC'} onChange={(e) => set({ questionType: e.target.value })}>
                            {types.map((tName) => (<option key={tName} value={tName}>{tName}</option>))}
                        </select>
                    </label>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={!!q.isMetric} onChange={(e) => set({ isMetric: e.target.checked })} />
                        <span>{t('editor.metric', { defaultValue: 'Metryczka' })}</span>
                    </label>
                </div>
                <label>
                    {t('editor.content', { defaultValue: 'Treść pytania' })}
                    <textarea className={styles.area} value={q.questionContent || ''} onChange={(e) => set({ questionContent: e.target.value })} />
                </label>
            </div>

            {/* Załączniki (nazwa pliku) + opcjonalna etykieta par nagrań */}
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.attachments', { defaultValue: 'Załączniki' })}</div>
                {(Array.isArray(q.attachments) ? q.attachments : (q.attachmentUrls || [])).map((stored, idx) => {
                    const toDisplay = (val) => {
                        if (!val) return '';
                        try {
                            // Try to parse URLs like /api/...&key=audio/3B.mp3 and extract filename
                            const hasQuery = String(val).includes('?');
                            if (hasQuery) {
                                const u = new URL(val, window.location.origin);
                                const key = u.searchParams.get('key');
                                if (key) {
                                    const parts = String(key).split('/');
                                    return parts[parts.length - 1] || '';
                                }
                            }
                        } catch { /* ignore */ }
                        const s = String(val);
                        const parts = s.split('/');
                        const last = parts[parts.length - 1] || '';
                        return last;
                    };
                    const toStored = (input) => {
                        const fn = String(input || '').trim();
                        if (!fn) return '';
                        // strip path if user pasted a URL or path
                        let file = fn;
                        try {
                            if (/^https?:\/\//i.test(fn)) {
                                const u = new URL(fn);
                                const key = u.searchParams.get('key');
                                if (key) {
                                    const kp = String(key).split('/');
                                    file = kp[kp.length - 1] || '';
                                } else {
                                    const p = u.pathname.split('/');
                                    file = p[p.length - 1] || fn;
                                }
                            } else if (fn.includes('/')) {
                                const p = fn.split('/');
                                file = p[p.length - 1] || fn;
                            }
                        } catch { /* keep fn */ }
                        if (!file) return '';
                        return file.startsWith('audio/') ? file : `audio/${file}`;
                    };
                    const display = toDisplay(stored);
                    return (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                            <input className={styles.field} value={display} placeholder={t('editor.attachmentFile', { defaultValue: 'Nazwa pliku (np. 3B.mp3)' })}
                                onChange={(e) => {
                                    const base = Array.isArray(q.attachments) ? q.attachments : (Array.isArray(q.attachmentUrls) ? q.attachmentUrls : []);
                                    const arr = base.slice();
                                    arr[idx] = toStored(e.target.value);
                                    set({ attachments: arr });
                                }} />
                            <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                                const base = Array.isArray(q.attachments) ? q.attachments : (q.attachmentUrls || []);
                                const arr = base.filter((_, i) => i !== idx);
                                set({ attachments: arr });
                            }}>✕</button>
                        </div>
                    );
                })}
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                        const base = Array.isArray(q.attachments) ? q.attachments : (q.attachmentUrls || []);
                        set({ attachments: [...base, 'audio/'] });
                    }}>+ {t('editor.attachment', { defaultValue: 'Załącznik' })}</button>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <span>{t('editor.attachmentLabel', { defaultValue: 'Etykieta nagrań' })}</span>
                        <input className={styles.field} style={{ maxWidth: 240 }} value={q?.details?.attachmentLabel || ''} onChange={(e) => setDetails({ attachmentLabel: e.target.value })} />
                    </label>
                </div>
            </div>

            {/* Details editors per type */}
            {q.questionType === 'SC' && (
                <TypeSC details={q.details} onChange={setDetails} />
            )}
            {q.questionType === 'MC' && (
                <TypeMC details={q.details} onChange={setDetails} />
            )}
            {q.questionType === 'Open' && (
                <TypeOpen details={q.details} onChange={setDetails} />
            )}
            {q.questionType === 'OpenList' && (
                <TypeOpenList details={q.details} onChange={setDetails} />
            )}
            {q.questionType === 'SelectText' && (
                <TypeSelectText details={q.details} onChange={setDetails} />
            )}
            {/* Label: no details/editor UI */}
            {q.questionType === 'Label' && null}

            {/* actions moved to header */}
        </div>
    );
}

function ArrayEditor({ label, values = [], onChange, placeholder }) {
    const add = () => onChange([...(values || []), '']);
    const setAt = (i, v) => onChange(values.map((x, idx) => idx === i ? v : x));
    const del = (i) => onChange(values.filter((_, idx) => idx !== i));
    return (
        <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontWeight: 600 }}>{label}</div>
            {(values || []).map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                    <input className={styles.field} style={{ flex: 1 }} value={v} placeholder={placeholder} onChange={(e) => setAt(i, e.target.value)} />
                    <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => del(i)}>✕</button>
                </div>
            ))}
            <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={add}>+ {label}</button>
        </div>
    );
}

function NumberListEditor({ label, values = [], onChange }) {
    const add = () => onChange([...(values || []), (values?.length || 0) + 1]);
    const setAt = (i, v) => onChange(values.map((x, idx) => idx === i ? Number(v) : x));
    const del = (i) => onChange(values.filter((_, idx) => idx !== i));
    return (
        <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontWeight: 600 }}>{label}</div>
            {(values || []).map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                    <input className={styles.field} style={{ width: 96 }} type="number" value={v} onChange={(e) => setAt(i, e.target.value)} />
                    <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => del(i)}>✕</button>
                </div>
            ))}
            <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={add}>+ {label}</button>
        </div>
    );
}

function TypeSC({ details = {}, onChange }) {
    const { t } = useTranslation();
    const { options = [], textFields = [], skipLogic = {} } = details;
    // Normalize options to objects {value, label}
    const normalizedOptions = options.map((opt, idx) => 
        typeof opt === 'string' ? { value: idx + 1, label: opt } : { value: opt.value ?? idx + 1, label: opt.label ?? '' }
    );
    const toggleTextField = (idx) => {
        const i = idx + 1; // 1-based
        const has = textFields.includes(i);
        const next = has ? textFields.filter((x) => x !== i) : [...textFields, i];
        onChange({ textFields: next });
    };
    return (
        <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.options', { defaultValue: 'Opcje' })}</div>
                {normalizedOptions.map((opt, idx) => {
                    const key = String(idx + 1);
                    const hasSkip = Number(skipLogic?.[key]) > 0;
                    return (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto 96px auto', gap: 8, alignItems: 'center' }}>
                            <input className={styles.field} value={opt.label} placeholder={t('editor.optionLabel', { defaultValue: 'Etykieta opcji' })} onChange={(e) => {
                                const next = normalizedOptions.slice(); next[idx] = { value: idx + 1, label: e.target.value }; onChange({ options: next });
                            }} />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input type="checkbox" checked={textFields.includes(idx + 1)} onChange={() => toggleTextField(idx)} />
                                <span>{t('editor.textField', { defaultValue: 'Pole tekstowe' })}</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input type="checkbox" checked={hasSkip} onChange={(e) => {
                                    const clone = { ...(skipLogic || {}) };
                                    if (!e.target.checked) delete clone[key]; else clone[key] = clone[key] || 2;
                                    onChange({ skipLogic: clone });
                                }} />
                                <span>{t('editor.skipTo', { defaultValue: 'Skocz do' })}</span>
                            </label>
                            <input className={styles.field} type="number" min={2} value={skipLogic?.[key] || ''} disabled={!hasSkip} onChange={(e) => {
                                const v = Number(e.target.value);
                                onChange({ skipLogic: { ...(skipLogic || {}), [key]: v } });
                            }} />
                            <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                                const next = normalizedOptions.filter((_, i) => i !== idx).map((o, i) => ({ value: i + 1, label: o.label })); 
                                onChange({ options: next });
                            }}>✕</button>
                        </div>
                    );
                })}
                <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                    const next = [...normalizedOptions, { value: normalizedOptions.length + 1, label: '' }];
                    onChange({ options: next });
                }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
            </div>
        </div>
    );
}

function TypeMC({ details = {}, onChange }) {
    const { t } = useTranslation();
    const { options = [], textFields = [] } = details;
    // Normalize options to objects {value, label}
    const normalizedOptions = options.map((opt, idx) => 
        typeof opt === 'string' ? { value: idx + 1, label: opt } : { value: opt.value ?? idx + 1, label: opt.label ?? '' }
    );
    const toggleTextField = (idx) => {
        const i = idx + 1;
        const has = textFields.includes(i);
        const next = has ? textFields.filter((x) => x !== i) : [...textFields, i];
        onChange({ textFields: next });
    };
    return (
        <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.options', { defaultValue: 'Opcje' })}</div>
                {normalizedOptions.map((opt, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}>
                        <input className={styles.field} value={opt.label} placeholder={t('editor.optionLabel', { defaultValue: 'Etykieta opcji' })} onChange={(e) => {
                            const next = normalizedOptions.slice(); next[idx] = { value: idx + 1, label: e.target.value }; onChange({ options: next });
                        }} />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={textFields.includes(idx + 1)} onChange={() => toggleTextField(idx)} />
                            <span>{t('editor.textField', { defaultValue: 'Pole tekstowe' })}</span>
                        </label>
                        <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => { 
                            const next = normalizedOptions.filter((_, i) => i !== idx).map((o, i) => ({ value: i + 1, label: o.label })); 
                            onChange({ options: next }); 
                        }}>✕</button>
                    </div>
                ))}
                <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                    const next = [...normalizedOptions, { value: normalizedOptions.length + 1, label: '' }];
                    onChange({ options: next });
                }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
            </div>
        </div>
    );
}

function TypeOpen({ details = {}, onChange }) {
    const { t } = useTranslation();
    const { answerType = 'str', options = [], textFields = [] } = details;
    // Normalize options to objects {value, label}
    const normalizedOptions = options.map((opt, idx) => 
        typeof opt === 'string' ? { value: idx + 1, label: opt } : { value: opt.value ?? idx + 1, label: opt.label ?? '' }
    );
    const minVal = Number.isFinite(details.min) ? details.min : 0;
    const maxVal = Number.isFinite(details.max) ? details.max : '';
    const toggleTextField = (idx) => {
        const i = idx + 1;
        const has = textFields.includes(i);
        const next = has ? textFields.filter((x) => x !== i) : [...textFields, i];
        onChange({ textFields: next });
    };
    return (
        <div style={{ display: 'grid', gap: 10 }}>
            <label>
                {t('editor.answerType', { defaultValue: 'Typ odpowiedzi' })}
                <select className={styles.select} value={answerType} onChange={(e) => {
                    const nextType = e.target.value;
                    onChange({ answerType: nextType, ...(nextType === 'int' ? { min: Number.isFinite(details.min) ? details.min : 0 } : {}) });
                }}>
                    <option value="str">str</option>
                    <option value="int">int</option>
                </select>
            </label>
            {answerType === 'int' && (
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
                    <label>
                        {t('editor.minValue', { defaultValue: 'Wartość min' })}
                        <input
                            className={styles.field}
                            type="number"
                            min={0}
                            value={minVal}
                            onChange={(e) => {
                                const raw = e.target.value;
                                const next = raw === '' ? 0 : Number(raw);
                                onChange({ min: Number.isFinite(next) ? Math.max(0, next) : 0 });
                            }}
                        />
                    </label>
                    <label>
                        {t('editor.maxValue', { defaultValue: 'Wartość max' })}
                        <input
                            className={styles.field}
                            type="number"
                            min={minVal}
                            value={maxVal}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    onChange({ max: undefined });
                                    return;
                                }
                                const next = Number(raw);
                                onChange({ max: Number.isFinite(next) ? next : undefined });
                            }}
                        />
                    </label>
                </div>
            )}
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.options', { defaultValue: 'Opcje' })}</div>
                {normalizedOptions.map((opt, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}>
                        <input className={styles.field} value={opt.label} placeholder={t('editor.optionLabel', { defaultValue: 'Etykieta opcji' })} onChange={(e) => {
                            const next = normalizedOptions.slice(); next[idx] = { value: idx + 1, label: e.target.value }; onChange({ options: next });
                        }} />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={textFields.includes(idx + 1)} onChange={() => toggleTextField(idx)} />
                            <span>{t('editor.textField', { defaultValue: 'Pole tekstowe' })}</span>
                        </label>
                        <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => { 
                            const next = normalizedOptions.filter((_, i) => i !== idx).map((o, i) => ({ value: i + 1, label: o.label })); 
                            onChange({ options: next }); 
                        }}>✕</button>
                    </div>
                ))}
                <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                    const next = [...normalizedOptions, { value: normalizedOptions.length + 1, label: '' }];
                    onChange({ options: next });
                }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
            </div>
        </div>
    );
}

function TypeOpenList({ details = {}, onChange }) {
    const { t } = useTranslation();
    const { answerType = 'str', options = [], textFields = [] } = details;
    // Normalize options to objects {value, label}
    const normalizedOptions = options.map((opt, idx) => 
        typeof opt === 'string' ? { value: idx + 1, label: opt } : { value: opt.value ?? idx + 1, label: opt.label ?? '' }
    );
    const rawSelect = Array.isArray(details.selectOptions) ? details.selectOptions : [];
    const normalizedSelect = rawSelect.map((opt, idx) => (
        typeof opt === 'object'
            ? { value: opt.value ?? (idx + 1), label: (opt.label ?? String(opt.value ?? '')) }
            : { value: idx + 1, label: String(opt ?? '') }
    ));
    const minVal = Number.isFinite(details.min) ? details.min : 0;
    const maxVal = Number.isFinite(details.max) ? details.max : '';
    const hasDropdown = Array.isArray(rawSelect) && rawSelect.length > 0;
    const toggleTextField = (idx) => {
        const i = idx + 1;
        const has = textFields.includes(i);
        const next = has ? textFields.filter((x) => x !== i) : [...textFields, i];
        onChange({ textFields: next });
    };
    return (
        <div style={{ display: 'grid', gap: 10 }}>
            <label>
                {t('editor.answerType', { defaultValue: 'Typ odpowiedzi' })}
                <select className={styles.select} value={answerType} onChange={(e) => {
                    const nextType = e.target.value;
                    onChange({ answerType: nextType, ...(nextType === 'int' ? { min: Number.isFinite(details.min) ? details.min : 0 } : {}) });
                }}>
                    <option value="str">str</option>
                    <option value="int">int</option>
                </select>
            </label>
            {answerType === 'int' && (
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
                    <label>
                        {t('editor.minValue', { defaultValue: 'Wartość min' })}
                        <input
                            className={styles.field}
                            type="number"
                            min={0}
                            value={minVal}
                            onChange={(e) => {
                                const raw = e.target.value;
                                const next = raw === '' ? 0 : Number(raw);
                                onChange({ min: Number.isFinite(next) ? Math.max(0, next) : 0 });
                            }}
                        />
                    </label>
                    <label>
                        {t('editor.maxValue', { defaultValue: 'Wartość max' })}
                        <input
                            className={styles.field}
                            type="number"
                            min={minVal}
                            value={maxVal}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    onChange({ max: undefined });
                                    return;
                                }
                                const next = Number(raw);
                                onChange({ max: Number.isFinite(next) ? next : undefined });
                            }}
                        />
                    </label>
                </div>
            )}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={hasDropdown} onChange={(e) => {
                    if (e.target.checked) {
                        const seed = normalizedSelect.length ? normalizedSelect : [{ value: 1, label: '' }];
                        onChange({ selectOptions: seed });
                    } else {
                        // Usuń dropdown z danych ankiety
                        onChange({ selectOptions: undefined });
                    }
                }} />
                <span>{t('editor.useDropdown', { defaultValue: 'Użyj listy rozwijalnej' })}</span>
            </label>
            {hasDropdown && (
                <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ fontWeight: 600 }}>{t('editor.dropdownOptions', { defaultValue: 'Opcje listy' })}</div>
                    {(normalizedSelect || []).map((opt, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                            <input className={styles.field} placeholder={t('editor.label', { defaultValue: 'Etykieta' })} value={opt.label}
                                onChange={(e) => {
                                    const next = normalizedSelect.slice();
                                    next[idx] = { ...opt, label: e.target.value };
                                    onChange({ selectOptions: next });
                                }} />
                            <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                                const next = normalizedSelect.filter((_, i) => i !== idx);
                                onChange({ selectOptions: next });
                            }}>✕</button>
                        </div>
                    ))}
                    <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                        const next = [...normalizedSelect, { value: normalizedSelect.length + 1, label: '' }];
                        onChange({ selectOptions: next });
                    }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
                </div>
            )}
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.options', { defaultValue: 'Opcje' })}</div>
                {normalizedOptions.map((opt, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}>
                        <input className={styles.field} value={opt.label} placeholder={t('editor.optionLabel', { defaultValue: 'Etykieta opcji' })} onChange={(e) => {
                            const next = normalizedOptions.slice(); next[idx] = { value: idx + 1, label: e.target.value }; onChange({ options: next });
                        }} />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={textFields.includes(idx + 1)} onChange={() => toggleTextField(idx)} />
                            <span>{t('editor.textField', { defaultValue: 'Pole tekstowe' })}</span>
                        </label>
                        <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => { 
                            const next = normalizedOptions.filter((_, i) => i !== idx).map((o, i) => ({ value: i + 1, label: o.label })); 
                            onChange({ options: next }); 
                        }}>✕</button>
                    </div>
                ))}
                <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                    const next = [...normalizedOptions, { value: normalizedOptions.length + 1, label: '' }];
                    onChange({ options: next });
                }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
            </div>
        </div>
    );
}

// Ladder has only question content, no details editor

function TypeSelectText({ details = {}, onChange }) {
    const { t } = useTranslation();
    const { options = [] } = details;
    // Normalize options to objects {value, label}
    const normalizedOptions = options.map((opt, idx) => 
        typeof opt === 'string' ? { value: idx + 1, label: opt } : { value: opt.value ?? idx + 1, label: opt.label ?? '' }
    );
    return (
        <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{t('editor.options', { defaultValue: 'Opcje' })}</div>
                {normalizedOptions.map((opt, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                        <input className={styles.field} value={opt.label} placeholder={t('editor.optionLabel', { defaultValue: 'Etykieta opcji' })} onChange={(e) => {
                            const next = normalizedOptions.slice(); next[idx] = { value: idx + 1, label: e.target.value }; onChange({ options: next });
                        }} />
                        <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => { 
                            const next = normalizedOptions.filter((_, i) => i !== idx).map((o, i) => ({ value: i + 1, label: o.label })); 
                            onChange({ options: next }); 
                        }}>✕</button>
                    </div>
                ))}
                <button type="button" className={`${styles.iconBtn} ${styles.btnPrimary}`} onClick={() => {
                    const next = [...normalizedOptions, { value: normalizedOptions.length + 1, label: '' }];
                    onChange({ options: next });
                }}>+ {t('editor.option', { defaultValue: 'Opcja' })}</button>
            </div>
        </div>
    );
}
